"use client"

import { useCallback } from "react"
import { useGlobalSlideoverStore } from "@/stores/use-global-slideover-store"

import MeetingDetailsPanel from "@/components/calendar/meeting-details-panel"
import { useNoteDialog } from "@/components/notes/use-note-dialog"
import { OpportunityLink } from "@/components/opportunities/opportunity-link"
import PeekView from "@/components/peek-view"
import { TaskDetailSlideover } from "@/components/tasks/detail-slideover"

const PEEK_VIEW_TYPES: Record<
  string,
  "Client" | "Prospect" | "PastClient" | "Household" | "Business" | "Contact" | "Person" | "Project"
> = {
  client: "Client",
  prospect: "Prospect",
  pastclient: "PastClient",
  household: "Household",
  business: "Business",
  contact: "Contact",
  person: "Person",
  project: "Project",
}

interface ObjectLinkComponentProps {
  objectType: string
  objectId: string
  label: string
}

export default function ObjectLinkComponent({ objectType, objectId, label }: ObjectLinkComponentProps) {
  const normalizedObjectType = objectType.toLowerCase()
  const peekViewType = PEEK_VIEW_TYPES[normalizedObjectType]

  if (normalizedObjectType === "opportunity") {
    const parts = objectId.split("/")
    if (parts.length !== 2 || parts.some((part) => !/^[a-zA-Z0-9]+$/.test(part))) return <span>{label}</span>
    return <OpportunityLink pipelineId={parts[0]} opportunityId={parts[1]} label={label} />
  }

  if (peekViewType) {
    return <PeekView recordType={peekViewType} recordId={objectId} label={label} size="base" />
  }

  return <ActionLink objectType={normalizedObjectType} objectId={objectId} label={label} />
}

// email_thread is intentionally excluded: nothing produces real email_thread
// object-links (HELP-781 — they were LLM hallucinations), so they render as
// plain text rather than a broken/cross-company thread link.
const ACTION_TYPES = new Set(["note", "task", "meeting", "storage_file"])

function ActionLink({ objectType, objectId, label }: ObjectLinkComponentProps) {
  const { open: openNoteDialog } = useNoteDialog()
  const { openSlideover, closeSlideover } = useGlobalSlideoverStore()

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      switch (objectType) {
        case "note":
          openNoteDialog({ noteId: objectId })
          return
        case "task":
          openSlideover({ content: <TaskDetailSlideover taskId={objectId} /> })
          return
        case "meeting":
          openSlideover({
            size: "md",
            content: (
              <MeetingDetailsPanel
                meeting={{ id: objectId } as Parameters<typeof MeetingDetailsPanel>[0]["meeting"]}
                onDelete={() => closeSlideover()}
                className="-mx-2! -mt-4!"
              />
            ),
          })
          return
        case "storage_file":
          try {
            const url = new URL(objectId)
            if (url.protocol === "https:" || url.protocol === "http:") {
              window.open(url.href, "_blank", "noopener,noreferrer")
            }
          } catch {
            // invalid URL, ignore
          }
          return
      }
    },
    [objectType, objectId, openNoteDialog, openSlideover, closeSlideover]
  )

  if (!ACTION_TYPES.has(objectType)) {
    return <span>{label}</span>
  }

  return (
    <a
      href="#"
      onClick={handleClick}
      className="cursor-pointer border-b border-dashed border-current/40 text-inherit transition-colors duration-150 hover:border-current"
    >
      {label}
    </a>
  )
}
